import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ProRace, STAGE_TYPE_COLORS, displayBundesland } from '@radevents/shared';
import { buildStageSchema, buildStageBreadcrumbSchema, stageLabel, stageRouteText } from '../hooks/useProRaceSchema';
import PublicHeader from '../components/Layout/PublicHeader';
import GpxMap from '../components/GpxMap';
import { StageAccommodationCard } from '../components/AccommodationCard';
import api from '../services/api';

import { SITE_ORIGIN } from '../config/siteOrigin';
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const FONT_DISPLAY = "'Archivo',sans-serif";
const FONT_BODY = "'Hanken Grotesk',sans-serif";
const FONT_MONO = "'Space Mono',monospace";

const STAGE_TYPE_KEY: Record<string, string> = {
  FLACH: 'Flach', HÜGELIG: 'Hügelig', BERG: 'Bergetappe', ZEITFAHREN: 'Zeitfahren', PROLOG: 'Zeitfahren',
};
const stageTypeLabel: Record<string, string> = {
  FLACH: 'Flachetappe', HÜGELIG: 'Hügelige Etappe', BERG: 'Bergetappe', ZEITFAHREN: 'Zeitfahren', PROLOG: 'Prolog',
};
function stageColor(t?: string): string {
  if (!t) return '#6A6A6A';
  return STAGE_TYPE_COLORS[STAGE_TYPE_KEY[t] ?? t] ?? '#6A6A6A';
}

function formatLongDate(d?: string): string | null {
  if (!d) return null;
  // Date-only strings must be parsed as local time ('T00:00:00'), not UTC —
  // otherwise the calendar day shifts around local midnight.
  return new Date(d + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}

function isToday(d?: string): boolean {
  if (!d) return false;
  const now = new Date();
  const date = new Date(d + 'T00:00:00');
  return now.getFullYear() === date.getFullYear()
    && now.getMonth() === date.getMonth()
    && now.getDate() === date.getDate();
}

export default function ProRaceStageDetail() {
  const { slug, stageNumber } = useParams<{ slug: string; stageNumber: string }>();
  const [race, setRace] = useState<ProRace | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Auch bei Etappenwechsel innerhalb desselben Rennens (slug bleibt gleich) nach oben.
  useEffect(() => { window.scrollTo(0, 0); }, [slug, stageNumber]);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.get(`/pro-races/${slug}`)
      .then(r => setRace(r.data))
      .catch(e => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{ minHeight: '100vh', background: '#F5F3F3', padding: 48, textAlign: 'center', color: '#6A6A6A' }}>Lade…</div>;

  const num = stageNumber != null ? parseInt(stageNumber, 10) : NaN;
  const stages = race?.stages ?? [];
  const stage = !Number.isNaN(num) ? stages.find(s => s.stageNumber === num) : undefined;

  if (notFound || !race || !stage) return (
    <div style={{ minHeight: '100vh', background: '#F5F3F3', padding: 48, textAlign: 'center' }}>
      <p style={{ color: '#6A6A6A', marginBottom: 16 }}>Etappe nicht gefunden.</p>
      {race
        ? <Link to={`/rennkalender/${race.slug}`} style={{ color: '#922C57' }}>← Zurück zum Rennen</Link>
        : <Link to="/rennkalender" style={{ color: '#922C57' }}>← Zurück zum Kalender</Link>}
    </div>
  );

  const sorted = [...stages].sort((a, b) => a.stageNumber - b.stageNumber);
  const idx = sorted.findIndex(s => s.stageNumber === stage.stageNumber);
  const prev = idx > 0 ? sorted[idx - 1] : null;
  const next = idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null;

  const label = stageLabel(stage);
  const route = stageRouteText(stage);
  const raceYear = new Date(race.startDate + 'T00:00:00').getFullYear();
  const live = isToday(stage.stageDate);
  const stageDateLong = formatLongDate(stage.stageDate);
  const typeLabel = stage.stageType ? (stageTypeLabel[stage.stageType] ?? stage.stageType) : null;
  const accent = stageColor(stage.stageType);

  const canonical = `${SITE_ORIGIN}/rennkalender/${race.slug}/etappe/${stage.stageNumber}`;
  const ogImage = race.imageUrl
    ? (race.imageUrl.startsWith('http') ? race.imageUrl : `${SITE_ORIGIN}${race.imageUrl}`)
    : null;
  const pageTitle = `${race.name} ${raceYear} – ${label}${route ? `: ${route}` : ''}`;
  const descBits = [
    route,
    stage.distanceKm != null ? `${stage.distanceKm} km` : '',
    stage.elevationMeters != null ? `${stage.elevationMeters} Hm` : '',
    race.broadcastInfo ? `TV: ${race.broadcastInfo}` : '',
  ].filter(Boolean);
  const pageDesc = `${label} der ${race.name} ${raceYear}${descBits.length ? `: ${descBits.join(', ')}` : ''}. Strecke, Höhenprofil, Zeitplan und Übertragung.`;

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta name="twitter:card" content={ogImage ? 'summary_large_image' : 'summary'} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}
        <script type="application/ld+json">{JSON.stringify(buildStageSchema(race, stage))}</script>
        <script type="application/ld+json">{JSON.stringify(buildStageBreadcrumbSchema(race, stage))}</script>
      </Helmet>

      <PublicHeader />

      <div style={{ flex: 1, width: '100%', boxSizing: 'border-box', maxWidth: 820, margin: '0 auto', padding: '24px 24px 60px' }}>
        <nav aria-label="Breadcrumb" style={{ fontFamily: FONT_MONO, fontSize: 12, color: '#6A6A6A', marginBottom: 16 }}>
          <Link to={`/rennkalender/${race.slug}`} style={{ color: '#6A6A6A', textDecoration: 'none' }}>‹ {race.name}</Link>
          <span> / </span>
          <span style={{ color: '#2C2C2C' }}>{label}</span>
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          {typeLabel && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: accent }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, transform: 'rotate(45deg)', background: accent }} />
              {typeLabel}
            </span>
          )}
          {live && (
            <span style={{ fontFamily: FONT_MONO, fontWeight: 700, fontSize: 12, color: '#A82F5A', background: 'rgba(194,80,120,0.12)', padding: '4px 11px', borderRadius: 999 }}>
              ● Heute
            </span>
          )}
        </div>

        <h1 style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 36, letterSpacing: '-0.015em', color: '#2C2C2C', margin: '12px 0 6px' }}>
          {label}{route ? `: ${route}` : ''}
        </h1>
        <div style={{ fontFamily: FONT_BODY, fontSize: 14, color: '#6A6A6A' }}>
          {stageDateLong && <time dateTime={stage.stageDate}>{stageDateLong}</time>}
          {stageDateLong && ' · '}{race.name}
        </div>

        {stage.hasGpx ? (
          <div style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, marginTop: 18, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderBottom: '1px solid #E5E1E1', gap: 12 }}>
              <span style={{ fontFamily: FONT_MONO, fontSize: 12, letterSpacing: '0.1em', color: '#2C2C2C', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                STRECKE{route ? ` · ${route}` : ''}
              </span>
            </div>
            <GpxMap gpxUrl={`${API_BASE}/api/v1/pro-races/${stage.proRaceId}/stages/${stage.stageNumber}/gpx`} />
          </div>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginTop: 14 }}>
          <div style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: 16 }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.1em', color: '#9A9494' }}>DISTANZ</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 20, color: '#2C2C2C', marginTop: 5 }}>
              {stage.distanceKm != null ? `${stage.distanceKm} km` : '–'}
            </div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: 16 }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.1em', color: '#9A9494' }}>HÖHE</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 20, color: '#2C2C2C', marginTop: 5 }}>
              {stage.elevationMeters != null ? `${stage.elevationMeters.toLocaleString('de-DE')} hm` : '–'}
            </div>
          </div>
          <div style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: 16 }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.1em', color: '#9A9494' }}>TYP</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 18, color: '#2C2C2C', marginTop: 5 }}>
              {typeLabel ?? '–'}
            </div>
          </div>
        </div>

        {(stage.startTime || stage.estimatedArrivalTime || stage.winner) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 14, marginTop: 14 }}>
            {(stage.startTime || stage.estimatedArrivalTime) && (
              <div style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: 16 }}>
                <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.1em', color: '#9A9494', marginBottom: 6 }}>🕒 ZEITPLAN</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {stage.startTime && <span style={{ fontFamily: FONT_BODY, fontSize: 14, color: '#4A4A4A' }}>Start: {stage.startTime}</span>}
                  {stage.estimatedArrivalTime && <span style={{ fontFamily: FONT_BODY, fontSize: 14, color: '#4A4A4A' }}>Zielankunft (ca.): {stage.estimatedArrivalTime}</span>}
                </div>
              </div>
            )}
            {stage.winner && (
              <div style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: 16 }}>
                <div style={{ fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.1em', color: '#9A9494', marginBottom: 6 }}>🏆 ETAPPENSIEGER</div>
                <span style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 15, color: '#2C2C2C' }}>{stage.winner}</span>
              </div>
            )}
          </div>
        )}

        {stage.description && (
          <div style={{ background: '#fff', border: '1px solid #E5E1E1', borderRadius: 14, padding: '20px 24px', marginTop: 14 }}>
            {stage.description.split(/\n\n+/).map((para, i) => (
              <p key={i} style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.7, color: '#4A4A4A', margin: i === 0 ? 0 : '14px 0 0' }}>{para}</p>
            ))}
          </div>
        )}

        {/* Unterkunft — Partnerlink, Zielort und Nacht nach der Etappe. Steht am Ende des
            Inhalts: erst lesen, worum es geht, dann die Frage nach dem Bett. */}
        <div style={{ marginTop: 14 }}>
          <StageAccommodationCard stage={stage} />
        </div>

        <nav aria-label="Etappen-Navigation" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 18 }}>
          {prev ? (
            <Link to={`/rennkalender/${race.slug}/etappe/${prev.stageNumber}`} style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, color: '#922C57', textDecoration: 'none' }}>
              ← {stageLabel(prev)}
            </Link>
          ) : <span />}
          {next ? (
            <Link to={`/rennkalender/${race.slug}/etappe/${next.stageNumber}`} style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, color: '#922C57', textDecoration: 'none', marginLeft: 'auto' }}>
              {stageLabel(next)} →
            </Link>
          ) : <span />}
        </nav>

        <div style={{ marginTop: 18 }}>
          <Link to={`/rennkalender/${race.slug}`} style={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: 14, color: '#922C57', textDecoration: 'none' }}>
            Alle Etappen & Gesamtinfos zur {race.name} →
          </Link>
        </div>

        {race.linkedEvents.length > 0 && (
          <section aria-label="Events für Hobbyfahrer" style={{ background: 'rgba(31,138,91,0.06)', border: '1px solid rgba(31,138,91,0.2)', borderRadius: 14, padding: '20px 24px', marginTop: 24 }}>
            <h2 style={{ fontFamily: FONT_DISPLAY, fontSize: 18, fontWeight: 800, margin: '0 0 6px', color: '#2C2C2C' }}>
              🚴 Selbst fahren statt nur zuschauen
            </h2>
            <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: '#6A6A6A', margin: '0 0 16px' }}>
              Rund um die {race.name} gibt es Jedermann-Veranstaltungen, bei denen du selbst mitfahren kannst:
            </p>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {race.linkedEvents.map(ev => (
                <li key={ev.id}>
                  <Link
                    to={`/events/${ev.id}`}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: '#fff', border: '1px solid #E5E1E1',
                      borderRadius: 10, padding: '12px 16px', textDecoration: 'none',
                    }}
                  >
                    <div>
                      <div style={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 14, color: '#2C2C2C' }}>{ev.title}</div>
                      <div style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#6A6A6A', marginTop: 2 }}>
                        <time dateTime={ev.eventDate}>{new Date(ev.eventDate + 'T00:00:00').toLocaleDateString('de-DE')}</time>
                        {ev.city && ` · ${ev.city}`}
                        {ev.bundesland && `, ${displayBundesland(ev.bundesland)}`}
                      </div>
                      {ev.note && <div style={{ fontFamily: FONT_BODY, fontSize: 12, color: '#9A9494', fontStyle: 'italic', marginTop: 2 }}>{ev.note}</div>}
                    </div>
                    <span style={{ color: '#C9C4C4', marginLeft: 12 }} aria-hidden="true">›</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

    </div>
  );
}
